import type { BillingPlan } from '@/api/services/billingAdmin';

export type BillingPlanDraft = {
  name: string;
  slug: string;
  description: string;
  isActive: boolean;
  priceCents: number;
  currency: string;
  billingInterval: string;
  maxSearches: number;
  maxSourcesPerSearch: number | null;
  maxNotificationsPerDay: number | null;
  maxNotificationRecipients: number | null;
  minScrapeIntervalMinutes: number | null;
  marketingTitle: string;
  badge: string;
  displayOrder: number;
  recommended: boolean;
  maxNotificationsPerMonth: number | null;
  maxTemplates: number | null;
  configJsonText: string;
};

export type BillingPlanFieldErrors = Partial<Record<keyof BillingPlanDraft | 'configJson', string>>;

export const BILLING_INTERVALS = [
  { value: 'month', label: 'Monthly' },
  { value: 'year', label: 'Yearly' },
  { value: 'one_time', label: 'One-time' },
  { value: 'custom', label: 'Custom' },
] as const;

export function emptyBillingPlanDraft(): BillingPlanDraft {
  return {
    name: '',
    slug: '',
    description: '',
    isActive: true,
    priceCents: 0,
    currency: 'EUR',
    billingInterval: 'month',
    maxSearches: 5,
    maxSourcesPerSearch: null,
    maxNotificationsPerDay: null,
    maxNotificationRecipients: null,
    minScrapeIntervalMinutes: null,
    marketingTitle: '',
    badge: '',
    displayOrder: 100,
    recommended: false,
    maxNotificationsPerMonth: null,
    maxTemplates: null,
    configJsonText: '{}',
  };
}

export function billingPlanToDraft(plan: BillingPlan): BillingPlanDraft {
  const cfg = plan.configJson ?? {};
  const extra = { ...cfg };
  delete extra.marketing_title;
  delete extra.badge;
  delete extra.display_order;
  delete extra.recommended;
  delete extra.max_notifications_per_month;
  delete extra.max_templates;

  return {
    name: plan.name,
    slug: plan.slug,
    description: plan.description ?? '',
    isActive: plan.isActive,
    priceCents: plan.priceCents,
    currency: plan.currency,
    billingInterval: plan.billingInterval,
    maxSearches: plan.maxSearches,
    maxSourcesPerSearch: plan.maxSourcesPerSearch ?? null,
    maxNotificationsPerDay: plan.maxNotificationsPerDay ?? null,
    maxNotificationRecipients: plan.maxNotificationRecipients ?? null,
    minScrapeIntervalMinutes: plan.minScrapeIntervalMinutes ?? null,
    marketingTitle: String(cfg.marketing_title ?? ''),
    badge: String(cfg.badge ?? ''),
    displayOrder: Number(cfg.display_order ?? 100),
    recommended: Boolean(cfg.recommended),
    maxNotificationsPerMonth:
      cfg.max_notifications_per_month == null ? null : Number(cfg.max_notifications_per_month),
    maxTemplates: cfg.max_templates == null ? null : Number(cfg.max_templates),
    configJsonText: JSON.stringify(extra, null, 2),
  };
}

export function validateBillingPlanDraft(
  draft: BillingPlanDraft,
  mode: 'create' | 'edit',
): BillingPlanFieldErrors {
  const errors: BillingPlanFieldErrors = {};
  if (!draft.name.trim()) errors.name = 'Name is required';
  if (!draft.slug.trim()) errors.slug = 'Slug is required';
  if (draft.priceCents < 0) errors.priceCents = 'Price must be >= 0';
  if (!draft.currency.trim()) errors.currency = 'Currency is required';
  if (!draft.billingInterval.trim()) errors.billingInterval = 'Interval is required';
  if (!Number.isFinite(draft.maxSearches) || draft.maxSearches <= 0) {
    errors.maxSearches = 'Max searches must be a positive integer';
  }
  if (mode === 'create' && !draft.slug.trim()) {
    errors.slug = 'Slug is required';
  }
  try {
    const parsed = JSON.parse(draft.configJsonText || '{}');
    if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      errors.configJson = 'Config JSON must be an object';
    }
  } catch {
    errors.configJson = 'Config JSON must be valid JSON';
  }
  return errors;
}

export function buildBillingPlanPayload(draft: BillingPlanDraft): Partial<BillingPlan> & {
  name: string;
  slug: string;
  maxSearches: number;
  configJson: Record<string, unknown>;
} {
  const extra = JSON.parse(draft.configJsonText || '{}') as Record<string, unknown>;
  const configJson: Record<string, unknown> = {
    ...extra,
    marketing_title: draft.marketingTitle.trim() || undefined,
    badge: draft.badge.trim() || undefined,
    display_order: draft.displayOrder,
    recommended: draft.recommended,
    max_notifications_per_month: draft.maxNotificationsPerMonth,
    max_templates: draft.maxTemplates,
  };
  Object.keys(configJson).forEach((key) => {
    if (configJson[key] === undefined || configJson[key] === '') delete configJson[key];
  });

  return {
    name: draft.name.trim(),
    slug: draft.slug.trim(),
    description: draft.description.trim() || null,
    isActive: draft.isActive,
    priceCents: draft.priceCents,
    currency: draft.currency.trim().toUpperCase(),
    billingInterval: draft.billingInterval,
    maxSearches: draft.maxSearches,
    maxSourcesPerSearch: draft.maxSourcesPerSearch,
    maxNotificationsPerDay: draft.maxNotificationsPerDay,
    maxNotificationRecipients: draft.maxNotificationRecipients,
    minScrapeIntervalMinutes: draft.minScrapeIntervalMinutes,
    configJson,
  };
}

export function formatQuota(value: number | null | undefined): string {
  if (value == null) return 'Unlimited';
  return String(value);
}

export function formatGrantPlanOptionLabel(plan: BillingPlan): string {
  const pricePart = formatGrantPlanPrice(plan);
  const searchPart =
    plan.slug === 'internal'
      ? 'unlimited searches'
      : `${plan.maxSearches} search${plan.maxSearches === 1 ? '' : 'es'}`;
  const sourcePart =
    plan.maxSourcesPerSearch == null
      ? 'Unlimited sources/search'
      : `${plan.maxSourcesPerSearch} sources/search`;
  return `${plan.name} — ${searchPart} — ${sourcePart} — ${pricePart}`;
}

function formatGrantPlanPrice(plan: BillingPlan): string {
  if (plan.slug === 'internal') return 'unlimited';
  if (plan.priceCents <= 0) return 'free';
  const amount = (plan.priceCents / 100).toLocaleString(undefined, {
    style: 'currency',
    currency: plan.currency || 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  const interval = plan.billingInterval === 'year' ? 'year' : 'month';
  return `${amount}/${interval}`;
}

export function expiryInputToIso(value: string): string | undefined {
  if (!value.trim()) return undefined;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
}

export function findPlanIdForSlug(plans: BillingPlan[], slug?: string | null): string {
  const normalized = slug?.trim().toLowerCase();
  if (!normalized) return plans.find((plan) => plan.isActive)?.id ?? plans[0]?.id ?? '';
  const match = plans.find((plan) => plan.slug === normalized);
  return match?.id ?? plans.find((plan) => plan.isActive)?.id ?? plans[0]?.id ?? '';
}
